import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from 'react-query';
import { X, Loader2, Navigation, Building2 } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { mapAPI } from '@shared/apiClient';

const parseLocalDate = (str) => {
  const [y, m, d] = String(str).split('-').map(Number);
  return new Date(y, m - 1, d);
};

const formatDayLabel = (str) =>
  parseLocalDate(str).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });

const fmtMin = (mins) => {
  const m = Math.max(0, Math.round(mins || 0));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
};

const shopIcon = L.divIcon({
  className: 'route-map-shop-marker',
  html: '<div style="background-color:#111827;color:white;border-radius:8px;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:bold;box-shadow:0 2px 6px rgba(0,0,0,0.35);border:2px solid white;">S</div>',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -14],
});

const stopIcon = (order, color) => L.divIcon({
  className: 'route-map-stop-marker',
  html: `<div style="background-color:${color};color:white;border-radius:50%;width:26px;height:26px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:bold;box-shadow:0 2px 6px rgba(0,0,0,0.35);border:2px solid white;">${order}</div>`,
  iconSize: [26, 26],
  iconAnchor: [13, 13],
  popupAnchor: [0, -13],
});

// Build the ordered list of route stops (shop -> unit centroids -> shop) for
// a single tech-day slot from an auto-schedule preview.
function buildStops(slot, preview) {
  const shopConfigured = Boolean(preview?.settings?.shopConfigured) && preview?.shopLat != null && preview?.shopLng != null;
  const returnToShop = Boolean(preview?.settings?.returnToShop);
  const stops = [];

  if (shopConfigured) {
    stops.push({ kind: 'shop', lat: preview.shopLat, lng: preview.shopLng, label: 'Shop', subLabel: preview.shopAddress });
  }

  slot.assignments.forEach((unit, idx) => {
    if (unit.lat == null || unit.lng == null) return;
    stops.push({
      kind: 'stop',
      order: idx + 1,
      lat: unit.lat,
      lng: unit.lng,
      label: unit.members.map((m) => m.displayName).join(' + '),
      subLabel: unit.members.map((m) => m.cityState).filter(Boolean).join(' · '),
      minutes: unit.totalMinutes,
      legTravelMinutes: unit.legTravelMinutes,
      callKinds: unit.callKinds,
    });
  });

  if (shopConfigured && returnToShop && slot.assignments.length > 0) {
    stops.push({ kind: 'shop', lat: preview.shopLat, lng: preview.shopLng, label: 'Shop (return)', subLabel: preview.shopAddress });
  }

  return stops;
}

export default function TechRouteMapModal({ slot, preview, color = '#2563eb', onClose }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const layerRef = useRef(null);

  const stops = useMemo(() => buildStops(slot, preview), [slot, preview]);
  const routableStops = stops.filter((s) => s.lat != null && s.lng != null);
  const stopsSignature = routableStops.map((s) => `${s.lat.toFixed(5)},${s.lng.toFixed(5)}`).join('|');

  const { data: routeData, isLoading } = useQuery(
    ['autoScheduleRoute', slot.date, slot.technicianId, stopsSignature],
    () => mapAPI.getRoute({ stops: routableStops.map((s) => ({ lat: s.lat, lng: s.lng })) }).then((res) => res.data),
    { enabled: routableStops.length >= 2, staleTime: 5 * 60 * 1000 }
  );

  // A straight-line fallback so a route line is always visible, even when
  // the road-routing service returns nothing (unconfigured/unreachable).
  const geometry = useMemo(() => {
    if (routeData?.geometry) return routeData.geometry;
    if (routableStops.length < 2) return null;
    return { type: 'LineString', coordinates: routableStops.map((s) => [s.lng, s.lat]) };
  }, [routeData, routableStops]);

  const usingRoadGeometry = Boolean(routeData?.geometry);

  useEffect(() => {
    if (!mapInstanceRef.current && mapRef.current) {
      mapInstanceRef.current = L.map(mapRef.current).setView([29.7604, -95.3698], 10);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(mapInstanceRef.current);
      layerRef.current = L.layerGroup().addTo(mapInstanceRef.current);
    }
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        layerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapInstanceRef.current || !layerRef.current) return;
    layerRef.current.clearLayers();
    const bounds = L.latLngBounds([]);

    if (geometry?.coordinates?.length) {
      const latLngs = geometry.coordinates.map(([lng, lat]) => [lat, lng]);
      L.polyline(latLngs, {
        color,
        weight: 4,
        opacity: 0.8,
        dashArray: usingRoadGeometry ? null : '6 5',
      }).addTo(layerRef.current);
    }

    routableStops.forEach((stop) => {
      const icon = stop.kind === 'shop' ? shopIcon : stopIcon(stop.order, color);
      const marker = L.marker([stop.lat, stop.lng], { icon });
      const minutesLine = stop.minutes != null
        ? `<p style="font-size:12px;margin:0 0 2px;">${stop.minutes}m on-site${stop.legTravelMinutes != null ? ` · ${Math.round(stop.legTravelMinutes)}m drive to get here` : ''}</p>`
        : '';
      marker.bindPopup(`
        <div style="padding:6px;min-width:180px;">
          <strong style="font-size:13px;">${stop.label}</strong>
          ${stop.subLabel ? `<p style="font-size:12px;color:#6b7280;margin:2px 0;">${stop.subLabel}</p>` : ''}
          ${minutesLine}
        </div>
      `);
      layerRef.current.addLayer(marker);
      bounds.extend([stop.lat, stop.lng]);
    });

    if (routableStops.length > 0) {
      mapInstanceRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    }
  }, [geometry, routableStops, color, usingRoadGeometry]);

  const skippedCount = stops.length - routableStops.length;
  const totalMiles = routeData?.distanceMeters != null ? (routeData.distanceMeters / 1609.34).toFixed(1) : null;

  return (
    <div className="fixed inset-0 z-[75] overflow-y-auto">
      <div className="flex items-start justify-center min-h-screen px-4 py-6">
        <div className="fixed inset-0 bg-gray-900/50" onClick={onClose} />

        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 inline-flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                {slot.technicianName}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {formatDayLabel(slot.date)}
                {' · '}
                {slot.assignments.reduce((sum, u) => sum + u.members.length, 0)} stop(s)
                {slot.driveMinutes ? ` · ${fmtMin(slot.driveMinutes)} drive` : ''}
                {totalMiles ? ` · ${totalMiles} mi` : ''}
              </p>
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-5 space-y-3 overflow-auto flex-1">
            {isLoading && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Computing road route…
              </div>
            )}
            {!isLoading && !usingRoadGeometry && routableStops.length >= 2 && (
              <div className="flex items-start gap-2 p-2.5 rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-600">
                <Navigation className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                Showing straight-line paths between stops — road routing was unavailable.
              </div>
            )}
            {skippedCount > 0 && (
              <div className="flex items-start gap-2 p-2.5 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800">
                {skippedCount} stop(s) omitted from the map — missing coordinates.
              </div>
            )}
            {preview?.settings?.shopConfigured && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <Building2 className="h-3.5 w-3.5" />
                Route starts{preview?.settings?.returnToShop ? ' and ends' : ''} at the shop{preview?.shopAddress ? ` — ${preview.shopAddress}` : ''}.
              </div>
            )}
            <div ref={mapRef} className="w-full rounded-lg border border-gray-300" style={{ height: '480px' }} />
          </div>
        </div>
      </div>
    </div>
  );
}
